// DeckEntry deliberately exports two pure helpers alongside the component.
// `seedKey` is the seed's identity rule, which BracketPage's handoff and this
// file must agree on exactly, and `fetchableHostsSentence` is the helper line
// the tests pin against the platform table. Both are one-liners over the same
// contract this file owns, and a sibling file to satisfy a fast-refresh rule
// would split the contract in two. Precedent: components/pro/ChatAttachments.tsx.
/* eslint-disable react-refresh/only-export-components */
import { useState, useCallback, useRef, useEffect } from 'react'
import { detectUrl, isFetchablePlatform, PLATFORM_NAMES, FETCHABLE_PLATFORM_ORDER } from '../../utils/urlDetector'
import i18n, { codeLabel, useLanguageTick } from '../../i18n'
import { fetchDeckFromUrl, ApiError } from '../../api/client'
import { mapFetchedSourceType, type FetchedDeckLink } from '../../lib/deckSync'
import PlatformGuide from './PlatformGuide'
import { HANDOFF_ATTR, usePrerenderHandoff } from '../../lib/prerenderHandoff'
import { EXAMPLE_DECK } from '../../data/exampleDeck'
import { isBasicLand } from '../../lib/basicLands'

export type DeckEntryOrigin = 'paste' | 'link' | 'file' | 'example'

/** External text to load exactly once, with its provenance. `origin: 'link'`
 *  means the text came from a fetched deck URL, whether or not that host
 *  yields a storable `link` (SlingLab does not). A deck fetched on Home and
 *  handed to /bracket unmarked keeps its link AND its origin this way. */
export interface DeckEntrySeed { decklist: string; link?: FetchedDeckLink; origin: DeckEntryOrigin }

/** The seed's full identity. Same decklist with a different link is a new
 *  seed (a second navigation), so it re-applies. */
export function seedKey(seed: DeckEntrySeed): string {
  return JSON.stringify([seed.decklist, seed.origin, seed.link?.sourceUrl ?? null, seed.link?.source ?? null])
}

export interface DeckEntryProps {
  /** 'compact' renders one row high and grows on focus or a multi-line paste,
   *  and STAYS grown until the field is cleared. It is the returning-home
   *  variant; everything else is 'full'. */
  /** 'full' is the analyzer's field: tall textarea, helper line, file
   *  upload. 'lean' is the Home hero: four rows, the button and the example,
   *  nothing else, because on a phone the helper sentence and the file row
   *  were most of the first screen for no added value. 'compact' is the
   *  returning hub's one-row field that grows on use. */
  variant?: 'full' | 'lean' | 'compact'
  /** The one exit. `link` is present only for a fetched deck on a LINKABLE
   *  host; `origin` says how the text arrived. Home hosts navigate, the
   *  Bracket host analyzes. */
  onDecklist: (decklist: string, link: FetchedDeckLink | undefined, origin: DeckEntryOrigin) => void
  isLoading?: boolean
  onCancel?: () => void
  onDecklistLoaded?: (text: string) => void
  seed?: DeckEntrySeed | null
  /** False for a fetched list means: load the text, keep its provenance, do
   *  not submit. The host's commander gate takes over. */
  canAutoAnalyze?: (decklist: string) => boolean
  /** When set, the button is disabled and this renders as the caption. */
  analyzeBlockedReason?: string | null
  /** The host's commander gate, rendered between the field and the button. */
  beforeAnalyze?: React.ReactNode
  /** Analytics injection. This file must never import lib/analytics: it is
   *  prerendered for /checker in Node (src/prerender/checker-entry.tsx), so
   *  the host passes trackEvent in and the checker passes nothing. */
  onEvent?: (name: string, payload: Record<string, string>) => void
  submitLabel?: string
  showExample?: boolean
}

/** "Moxfield, Archidekt, ManaBox, DeckCheck, CommanderSalt, Hareruya, EDHREC and SlingLab" */
export function fetchableHostsSentence(): string {
  const names = FETCHABLE_PLATFORM_ORDER.map(p => PLATFORM_NAMES[p])
  if (names.length <= 1) return names.join('')
  return i18n.t('common:input.list_and', { items: names.slice(0, -1).join(', '), last: names[names.length - 1] })
}

/** The hosted (linkable) hosts, for the unsupported-link message. Same list
 *  SUPPORTED_DECK_LINKS_COPY in utils/urlDetector names, whose English the
 *  message reproduces exactly. */
function hostedHostsList(): string {
  // eslint-disable-next-line i18next/no-literal-string -- a platform code
  return FETCHABLE_PLATFORM_ORDER.filter(p => p !== 'slinglab').map(p => PLATFORM_NAMES[p]).join(', ')
}

/** How a status line is colored. Decided where the status is SET, never by
 *  reading its (translated) text back. */
type StatusTone = 'ok' | 'error' | 'info'
interface Status { text: string; tone: StatusTone }
/* eslint-disable i18next/no-literal-string -- CSS classes */
const STATUS_CLASS: Record<StatusTone, string> = {
  ok: 'bg-green-900/30 text-green-400',
  error: 'bg-red-900/30 text-red-400',
  info: 'bg-blue-900/30 text-blue-400',
}
/* eslint-enable i18next/no-literal-string */

const isSingleLine = (text: string) => !text.trim().includes('\n')

/** Provenance of the text currently in the field, when that text is exactly a
 *  fetched (or fetched-and-seeded) deck. `link` may be undefined (SlingLab)
 *  while the origin is still 'link'; that is why this is one object and not
 *  a bare link. Any manual edit, file load or example clears it. */
interface Pending { decklist: string; link: FetchedDeckLink | undefined }

export default function DeckEntry({
  variant = 'full', onDecklist, isLoading = false, onCancel, onDecklistLoaded, seed,
  canAutoAnalyze, analyzeBlockedReason, beforeAnalyze, onEvent, submitLabel, showExample = true,
}: DeckEntryProps) {
  useLanguageTick()
  const [text, setText] = useState('')
  const [fetching, setFetching] = useState(false)
  const [status, setStatus] = useState<Status | null>(null)
  const [showGuide, setShowGuide] = useState<ReturnType<typeof detectUrl> | null>(null)
  // Compact growth is LATCHED: focus or a newline opens the field, and only an
  // empty field closes it again. A blur must never collapse it, because the
  // collapse would swap the button node out between mousedown and click.
  const [grown, setGrown] = useState(variant !== 'compact')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fieldRef = useRef<HTMLTextAreaElement>(null)
  const pending = useRef<Pending | null>(null)
  // How the text currently in the field arrived. 'file' text is never a link
  // to fetch, even when its one line is a URL, and submits with the file
  // origin; any manual edit makes it 'paste' again.
  // eslint-disable-next-line i18next/no-literal-string -- an origin code
  const textOrigin = useRef<DeckEntryOrigin>('paste')
  // Request token. A file load, a seed or an example bumps it, so a fetch
  // that was still in flight cannot land on top of what the user chose last.
  const fetchGen = useRef(0)
  const lastSeedKey = useRef<string | null>(null)
  // Re-armed on every setup (StrictMode replays cleanup + setup); a fetch that
  // resolves after unmount must not call onDecklist.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    if (!seed || !seed.decklist.trim()) return
    const key = seedKey(seed)
    if (key === lastSeedKey.current) return
    lastSeedKey.current = key
    // Only a link-origin seed carries provenance; a pasted seed is a paste.
    fetchGen.current += 1
    setFetching(false)
    pending.current = seed.origin === 'link' ? { decklist: seed.decklist, link: seed.link } : null
    // eslint-disable-next-line i18next/no-literal-string -- origin codes
    textOrigin.current = seed.origin === 'file' ? 'file' : 'paste'
    setText(seed.decklist)
    setGrown(true)
    onDecklistLoaded?.(seed.decklist)
  }, [seed, onDecklistLoaded])

  const emit = (name: string, payload: Record<string, string>) => onEvent?.(name, payload)

  const setValue = (value: string) => {
    pending.current = null
    // eslint-disable-next-line i18next/no-literal-string -- an origin code
    textOrigin.current = 'paste'
    setShowGuide(null)
    setStatus(null)
    setText(value)
    if (value.includes('\n')) setGrown(true)
    if (!value.trim() && variant === 'compact') setGrown(false)
    onDecklistLoaded?.(value)
  }

  // A deck pasted into the prerendered field before the app booted arrives
  // here once, through the same setValue a keystroke uses (src/lib/
  // prerenderHandoff.ts), with its focus and selection.
  // eslint-disable-next-line i18next/no-literal-string -- a handoff key
  usePrerenderHandoff('deck-entry', fieldRef, setValue)

  const fetchLink = useCallback(async (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed || fetching || isLoading) return
    const detected = detectUrl(trimmed)
    if (!isFetchablePlatform(detected.platform)) return
    const platformName = PLATFORM_NAMES[detected.platform]
    const token = ++fetchGen.current
    const current = () => alive.current && token === fetchGen.current
    setFetching(true)
    setShowGuide(null)
    // eslint-disable-next-line i18next/no-literal-string -- a tone code
    setStatus({ text: i18n.t('common:input.status.fetching', { platform: platformName }), tone: 'info' })
    try {
      const result = await fetchDeckFromUrl(detected.url)
      if (!current()) return
      const decklist = result.decklist
      const mappedSource = mapFetchedSourceType(result.source_type)
      const link = mappedSource ? { sourceUrl: result.source_url, source: mappedSource } : undefined
      // eslint-disable-next-line i18next/no-literal-string -- an origin code
      textOrigin.current = 'paste'
      setText(decklist)
      setGrown(true)
      onDecklistLoaded?.(decklist)
      setStatus(null)
      // eslint-disable-next-line i18next/no-literal-string -- analytics codes
      emit('home_check_deck', { kind: 'url' })
      if (canAutoAnalyze && !canAutoAnalyze(decklist)) {
        pending.current = { decklist, link }
        return
      }
      pending.current = null
      // eslint-disable-next-line i18next/no-literal-string -- an origin code
      onDecklist(decklist, link, 'link')
    } catch (e) {
      if (!current()) return
      // eslint-disable-next-line i18next/no-literal-string -- analytics codes
      emit('home_check_deck', { kind: 'url_failed' })
      // A SlingLab ?deckId= link is refused with a 400 whose `detail` says it
      // needs a SlingLab login: the only failure detail a user can act on.
      const slinglabDetail = detected.platform === 'slinglab' && e instanceof ApiError ? e.detail : undefined
      // A machine code (a localized response, i18n 3-api.2) reads from the
      // web catalog, `common:input.source_error.<code>`; an English response
      // carries none, so its message is exactly what it was before.
      const detail = slinglabDetail
        ?? (e instanceof ApiError && e.code ? codeLabel('common', 'input.source_error', e.code) ?? undefined : undefined)
      setStatus({
        text: detail
          ? i18n.t('common:input.status.fetch_failed_detail', { platform: platformName, detail })
          : i18n.t('common:input.status.fetch_failed', { platform: platformName }),
        // eslint-disable-next-line i18next/no-literal-string -- a tone code
        tone: 'error',
      })
    } finally {
      if (current()) setFetching(false)
    }
    // onDecklist/onEvent are host callbacks recreated per render; listing them
    // would defeat the memo for no behavioural gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetching, isLoading, canAutoAnalyze, onDecklistLoaded])

  const handleSubmit = (override?: string, origin: DeckEntryOrigin = 'paste') => {
    const trimmed = (override ?? text).trim()
    if (!trimmed || isLoading || fetching) return
    if (override === undefined && textOrigin.current === 'file') {
      // A file is text. No link detection, no provenance, the file origin.
      // eslint-disable-next-line i18next/no-literal-string -- an origin code
      onDecklist(trimmed, undefined, 'file')
      return
    }
    if (isSingleLine(trimmed)) {
      const detected = detectUrl(trimmed)
      // Order matters. Fetchable first; then 'unknown' (any http(s) URL the
      // detector does not recognise) gets the supported-hosts line; then a
      // KNOWN export-only host (TappedOut, Deckstats) gets its export guide.
      // 'none' is not a URL at all and falls through to the decklist path.
      if (isFetchablePlatform(detected.platform)) { void fetchLink(trimmed); return }
      if (detected.platform === 'unknown') {
        // eslint-disable-next-line i18next/no-literal-string -- a tone code
        setStatus({ text: i18n.t('common:input.status.unsupported', { hosts: hostedHostsList() }), tone: 'error' })
        return
      }
      if (detected.platform !== 'none') { setShowGuide(detected); return }
    }
    const p = pending.current
    if (p && p.decklist.trim() === trimmed) {
      // Kept, not consumed: if the host's analysis fails and the user submits
      // the same text again, the deck is still the fetched deck. Any edit,
      // file load or example clears it; a successful analysis unmounts us.
      // eslint-disable-next-line i18next/no-literal-string -- an origin code
      onDecklist(trimmed, p.link, 'link')
      return
    }
    // eslint-disable-next-line i18next/no-literal-string -- analytics codes
    if (origin === 'paste') emit('home_check_deck', { kind: 'text' })
    onDecklist(trimmed, undefined, origin)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line i18next/no-literal-string -- a MIME format
    const pasted = e.clipboardData.getData('text').trim()
    if (!pasted || !isSingleLine(pasted)) return
    if (isFetchablePlatform(detectUrl(pasted).platform)) {
      e.preventDefault()
      // A deliberate paste of a link over whatever was here (file text
      // included) is a paste: if the fetch fails, the next submit must retry
      // the link, not hand the URL over as a file.
      // eslint-disable-next-line i18next/no-literal-string -- an origin code
      textOrigin.current = 'paste'
      pending.current = null
      setText(pasted)
      void fetchLink(pasted)
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result
      if (typeof content !== 'string' || !alive.current) return
      // A file is text, never a link to fetch, even when its one line is a URL.
      // It also outranks a fetch still in flight.
      fetchGen.current += 1
      setFetching(false)
      pending.current = null
      // eslint-disable-next-line i18next/no-literal-string -- an origin code
      textOrigin.current = 'file'
      setText(content)
      setGrown(true)
      onDecklistLoaded?.(content)
      setShowGuide(null)
      // eslint-disable-next-line i18next/no-literal-string -- a tone code
      setStatus({ text: i18n.t('common:input.status.loaded', { name: file.name }), tone: 'ok' })
      setTimeout(() => { if (alive.current) setStatus(null) }, 3000)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const loadExample = () => {
    if (isLoading || fetching) return
    pending.current = null
    // eslint-disable-next-line i18next/no-literal-string -- an origin code
    textOrigin.current = 'paste'
    setText(EXAMPLE_DECK)
    setGrown(true)
    onDecklistLoaded?.(EXAMPLE_DECK)
    // eslint-disable-next-line i18next/no-literal-string -- an origin code
    handleSubmit(EXAMPLE_DECK, 'example')
  }

  const { cardCount, warnings } = countCards(text)
  const multiLine = text.includes('\n')
  const collapsed = variant === 'compact' && !grown
  const busy = isLoading || fetching
  const disabled = !text.trim() || busy || !!analyzeBlockedReason

  const primaryButton = (compactRow: boolean) => (
    <button
      type="button"
      onClick={() => handleSubmit()}
      disabled={disabled}
      className={compactRow
        ? 'shrink-0 py-2.5 px-4 rounded-lg font-semibold text-sm bg-purple-600 text-white hover:bg-purple-500 disabled:bg-purple-600/40 disabled:cursor-not-allowed transition-colors'
        : 'flex-1 py-3 px-6 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-base shadow-lg shadow-purple-900/30'}
    >
      {busy ? (
        <span className="flex items-center justify-center gap-2">
          <span className="animate-spin inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full" />
          {fetching ? i18n.t('common:input.fetching') : i18n.t('common:input.checking')}
        </span>
      ) : submitLabel ?? i18n.t('common:input.submit')}
    </button>
  )

  return (
    <div className={variant === 'full' ? 'surface p-4 sm:p-5 space-y-4' : variant === 'lean' ? 'surface p-4 space-y-3' : 'surface p-2 space-y-2'}>
      {/* Collapsed compact: field and button in one row from sm up, stacked
          below it. Grown: the field alone, everything else follows. */}
      <div className={collapsed ? 'flex flex-col sm:flex-row gap-2 sm:items-center' : 'space-y-3'}>
        <div className="relative flex-1 min-w-0">
          <textarea
            ref={fieldRef}
            // eslint-disable-next-line i18next/no-literal-string -- a handoff key
            {...{ [HANDOFF_ATTR]: 'deck-entry' }}
            value={text}
            rows={collapsed ? 1 : 4}
            onChange={e => setValue(e.target.value)}
            onPaste={handlePaste}
            onFocus={() => { if (variant === 'compact') setGrown(true) }}
            onKeyDown={e => { if (variant === 'compact' && e.key === 'Enter' && !e.shiftKey && !multiLine) { e.preventDefault(); handleSubmit() } }}
            placeholder={i18n.t('common:input.placeholder')}
            aria-label={i18n.t('common:input.field_label')}
            spellCheck={false}
            disabled={fetching}
            // Short screens get a 120px field so the submit button clears the
            // 90px desktop ad banner on the first screen (laptops ~760px tall).
            className={`w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-sm text-gray-100 placeholder-gray-500 focus:outline focus:outline-2 focus:outline-purple-500/60 transition-colors ${collapsed ? 'resize-none' : variant === 'full' ? 'min-h-[200px] [@media(max-height:860px)]:min-h-[120px] resize-y' : 'resize-none'} ${multiLine ? 'font-mono' : ''}`}
            // eslint-disable-next-line i18next/no-literal-string -- a CSS value
            style={{ fontSize: '16px' }}
          />
          {cardCount > 0 && multiLine && (
            <div className="absolute bottom-3 right-3 text-xs font-medium text-gray-300 bg-black/40 rounded-full px-2 py-0.5">{i18n.t('common:input.card_count', { n: cardCount })}</div>
          )}
        </div>
        {collapsed && primaryButton(true)}
      </div>

      {status && (
        <div className={`text-sm px-3 py-2 rounded-md ${STATUS_CLASS[status.tone]}`}>{status.text}</div>
      )}

      {showGuide && <PlatformGuide detected={showGuide} onDismiss={() => { setShowGuide(null); setValue('') }} />}

      {variant === 'full' && (
        <p className="text-xs text-gray-500">
          {i18n.t('common:input.helper', { hosts: fetchableHostsSentence() })}
        </p>
      )}

      {variant === 'full' && (
        <>
          <input ref={fileInputRef} type="file" accept=".txt,.csv,.dec,.dek,.mwDeck,.cod" onChange={handleFileUpload} className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="w-full py-2.5 bg-black/20 border border-white/10 border-dashed rounded-lg text-sm text-gray-400 hover:text-gray-200 hover:border-white/25 transition-colors"
          >
            {i18n.t('common:input.upload')}
          </button>
        </>
      )}

      {warnings.length > 0 && multiLine && !isLoading && (
        <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-md px-3 py-2 space-y-0.5">
          {warnings.map((w, i) => <p key={i} className="text-xs text-yellow-400">{w}</p>)}
        </div>
      )}

      {beforeAnalyze}

      {!collapsed && (
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[12rem] flex">{primaryButton(false)}</div>
          {isLoading && onCancel && (
            <button type="button" onClick={onCancel} className="px-4 py-3 bg-gray-800 border border-gray-700 text-gray-400 font-medium rounded-lg hover:bg-gray-700 hover:text-gray-200 transition-colors text-sm">{i18n.t('common:input.cancel')}</button>
          )}
          {showExample && variant === 'full' && (
            <button type="button" onClick={loadExample} disabled={busy} className="text-sm font-medium text-purple-300 hover:text-purple-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
              {i18n.t('common:input.example')}
            </button>
          )}
        </div>
      )}

      {showExample && variant === 'lean' && (
        <button type="button" onClick={loadExample} disabled={busy} className="w-full text-sm font-medium text-purple-300 hover:text-purple-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          {i18n.t('common:input.example')}
        </button>
      )}

      {analyzeBlockedReason && <p className="text-xs text-yellow-400/80 text-center">{analyzeBlockedReason}</p>}
    </div>
  )
}

// Counts copies, skips sideboard/maybeboard sections, and exempts basics from
// the duplicate warning.
/* eslint-disable i18next/no-literal-string -- decklist section headers and basic land names are parse codes */
function countCards(text: string): { cardCount: number; warnings: string[] } {
  let count = 0
  let inExcluded = false
  const seenCards = new Map<string, number>()
  const warns: string[] = []
  for (const line of text.split('\n')) {
    const trimmed = line.trim().toLowerCase()
    if (!trimmed) continue
    if (trimmed.startsWith('//')) {
      inExcluded = trimmed.includes('sideboard') || trimmed.includes('maybeboard') || trimmed.includes('maybe')
      continue
    }
    if (['sideboard', 'sideboard:', 'maybeboard', 'maybeboard:'].includes(trimmed)) { inExcluded = true; continue }
    if (['commander', 'commander:', 'mainboard', 'mainboard:', 'deck', 'deck:'].includes(trimmed)) { inExcluded = false; continue }
    if (inExcluded) continue
    const qtyMatch = line.trim().match(/^(\d+)x?\s/)
    const qty = qtyMatch ? parseInt(qtyMatch[1], 10) : 1
    count += qty
    const cardName = line.trim().replace(/^\d+x?\s+/, '').replace(/\s*\(.*$/, '').replace(/\s*\*[A-Za-z]+\*\s*$/, '').trim().toLowerCase()
    if (cardName) seenCards.set(cardName, (seenCards.get(cardName) ?? 0) + 1)
  }
  // Any basic land is exempt from singleton (one shared list, Snow-Covered
  // Wastes included).
  const dupes = [...seenCards.entries()].filter(([name, n]) => n > 1 && !isBasicLand(name)).map(([name]) => name)
  /* eslint-enable i18next/no-literal-string */
  if (dupes.length > 0 && dupes.length <= 3) warns.push(i18n.t('common:input.warn.duplicates', { names: dupes.join(', ') }))
  else if (dupes.length > 3) warns.push(i18n.t('common:input.warn.duplicate_count', { n: dupes.length }))
  if (count > 0 && count < 60) warns.push(i18n.t('common:input.warn.short', { n: count }))
  return { cardCount: count, warnings: warns }
}
